// "What to do next" for a cancellation ticket, in plain words. Reads each
// flight's own status (a Whole Trip ticket also handles the linked second
// flight), picks the first flight that needs the agent, and says what to do.

const DONE = ["cancellation_processed", "refund_processed", "cancelled_at_airline"];
const CLOSED = ["customer_declined", "abandoned", "expired", "blocked"];
const NEEDS_FEE = ["", "pending_charges", "charges_quoted", "charges_calculated", "dgca_hold"];

const isDgca = (t) => t?.is_dgca_eligible === true || Number(t?.is_dgca_eligible) === 1;
const tboOk = (t) => Number(t?.tbo_dgca_confirmed) === 1;

export function plainStatus(t) {
  const s = t?.cancellation_status || "";
  if (DONE.includes(s)) return { text: "Cancelled and refunded", tone: "green" };
  if (CLOSED.includes(s)) return { text: s === "expired" ? "Customer did not reply in time" : "Customer did not go ahead", tone: "gray" };
  if (s === "customer_accepted") {
    return t?.status === "Escalated"
      ? { text: "Customer accepted: cancel manually", tone: "red" }
      : { text: "Customer accepted: cancelling", tone: "blue" };
  }
  if (s === "charges_notified") return { text: "Waiting for customer", tone: "amber" };
  if (isDgca(t) && !tboOk(t)) return { text: "Check with TBO", tone: "red" };
  return { text: "Send refund to customer", tone: "red" };
}

function actionFor(t, label) {
  const s = t?.cancellation_status || "";
  const where = label ? ` for ${label}` : "";
  if (NEEDS_FEE.includes(s) && isDgca(t) && !tboOk(t)) {
    return {
      title: `Check with TBO${where}`,
      text:
        "This is a free cancellation (DGCA 48-hour rule), so the airline charges nothing. Ask TBO to confirm the airline " +
        `will refund the full fare, then click "Confirm TBO Refund"${label ? ` in ${label}` : ""} and enter their reference number.`,
    };
  }
  if (NEEDS_FEE.includes(s)) {
    return isDgca(t)
      ? {
          title: `Send the refund amount to the customer${where}`,
          text: `TBO has confirmed. In ${label || "the steps below"}, send the customer the refund with no cancellation fee so they can accept.`,
        }
      : {
          title: `Get the cancellation fee${where}`,
          text: "Get the airline's cancellation fee from TBO, check the refund, and send it to the customer to accept.",
        };
  }
  if (s === "customer_accepted" && t?.status === "Escalated") {
    return {
      title: `Cancel manually${where}`,
      text: "The customer accepted, but the automatic cancellation did not finish. Read the latest activity, cancel with TBO, and refund the customer.",
    };
  }
  return null;
}

const TONES = {
  green: "bg-green-100 text-green-800 border-green-200",
  red: "bg-red-100 text-red-800 border-red-200",
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  gray: "bg-gray-100 text-gray-700 border-gray-200",
};

export function StatusChip({ ticket }) {
  const st = plainStatus(ticket);
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONES[st.tone]}`}>{st.text}</span>;
}

const after = (t, statuses) => statuses.includes(t?.cancellation_status || "");
const SENT_OR_LATER = ["charges_notified", "customer_accepted", ...DONE];
const ACCEPTED_OR_LATER = ["customer_accepted", ...DONE];

/**
 * The whole job as numbered steps, in the order the agent does them:
 * per flight, check with TBO (free DGCA cancellation) or get the airline fee;
 * then send the refund, the customer accepts, and the system cancels and
 * refunds. Each step is done, current (the first one not done) or to come.
 */
export function jobSteps(flights) {
  const steps = flights.map(({ t, label }) => {
    const where = label ? ` for ${label}` : "";
    return isDgca(t)
      ? { text: `Check with TBO${where}`, done: tboOk(t) || after(t, SENT_OR_LATER) }
      : { text: `Get the airline's cancellation fee${where}`, done: after(t, SENT_OR_LATER) };
  });
  const all = (pred) => flights.every(({ t }) => pred(t));
  steps.push({
    text: flights.length > 1 ? "Send the refund to the customer (both flights)" : "Send the refund to the customer",
    done: all((t) => after(t, SENT_OR_LATER)),
  });
  steps.push({ text: "Customer accepts in the app", done: all((t) => after(t, ACCEPTED_OR_LATER)) });
  const stuck = flights.some(({ t }) => t.cancellation_status === "customer_accepted" && t.status === "Escalated");
  steps.push({
    text: stuck ? "Cancel and refund (do it manually: automatic cancellation did not finish)" : "Cancel and refund (happens automatically)",
    done: all((t) => after(t, DONE)),
  });
  const current = steps.findIndex((x) => !x.done);
  return steps.map((x, i) => ({ ...x, state: x.done ? "done" : i === current ? "current" : "todo" }));
}

function StepList({ steps }) {
  return (
    <ol className="mt-3 space-y-1.5" data-testid="job-steps">
      {steps.map((x, i) => (
        <li key={x.text} className="flex items-center gap-2 text-sm">
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
              x.state === "done"
                ? "bg-green-600 text-white"
                : x.state === "current"
                ? "bg-blue-600 text-white ring-4 ring-blue-200"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            {x.state === "done" ? "✓" : i + 1}
          </span>
          <span
            className={
              x.state === "done"
                ? "text-gray-500 line-through"
                : x.state === "current"
                ? "font-semibold text-gray-900"
                : "text-gray-500"
            }
          >
            Step {i + 1}: {x.text}
          </span>
          {x.state === "current" ? (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-800">Now</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export default function NextActionCard({ ticket, companion = null }) {
  if (!ticket) return null;
  const flights = companion ? [{ t: ticket, label: "Flight 1" }, { t: companion, label: "Flight 2" }] : [{ t: ticket, label: "" }];

  let next = null;
  for (const f of flights) {
    next = actionFor(f.t, f.label);
    if (next) break;
  }
  const all = (pred) => flights.every((f) => pred(f.t));
  if (!next) {
    if (all((t) => DONE.includes(t.cancellation_status))) {
      const claim = flights.some((f) => f.t.status === "Escalated");
      next = {
        title: claim ? "Cancelled and refunded: one follow-up left" : "Done: cancelled and refunded",
        text: claim
          ? "The customer has their refund. TBO still charged the airline fee on a free (DGCA) cancellation, so raise a DGCA claim with TBO. See the latest activity."
          : "Nothing more to do. Close the ticket when you are ready.",
        tone: claim ? "amber" : "green",
      };
    } else if (all((t) => CLOSED.includes(t.cancellation_status))) {
      next = { title: "Closed: nothing was cancelled", text: "The customer did not go ahead, so the booking stays as it is.", tone: "gray" };
    } else if (flights.some((f) => f.t.cancellation_status === "charges_notified")) {
      next = {
        title: "Waiting for the customer to accept",
        text: companion
          ? "The customer sees one total for both flights in the app and confirms once. This page updates when they reply."
          : "The customer has the refund amount in the app. This page updates when they reply.",
        tone: "amber",
      };
    } else {
      next = { title: "Cancellation in progress", text: "The system is cancelling with the airline. This page updates on its own.", tone: "blue" };
    }
  }
  const tone = next.tone || "red";
  const box = {
    red: "border-red-300 bg-red-50",
    amber: "border-amber-300 bg-amber-50",
    green: "border-green-300 bg-green-50",
    blue: "border-blue-300 bg-blue-50",
    gray: "border-gray-300 bg-gray-50",
  }[tone];

  return (
    <div className={`rounded-2xl border-2 px-5 py-4 ${box}`} data-testid="next-action">
      <div className="text-xs font-bold uppercase tracking-wide text-gray-500">What to do next</div>
      <div className="mt-1 text-lg font-bold text-gray-900">{next.title}</div>
      <p className="mt-1 text-sm text-gray-700">{next.text}</p>
      {!flights.every((f) => CLOSED.includes(f.t.cancellation_status)) && <StepList steps={jobSteps(flights)} />}
      {flights.length > 1 || flights[0].label ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {flights.map((f) => (
            <span key={f.label} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <span className="font-semibold">{f.label}:</span> <StatusChip ticket={f.t} />
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
